import { FC, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CompanionMascot from "./CompanionMascot";
import {
	AGENT_COMPANION_FULL_SIZE,
	AGENT_COMPANION_PEEK_SIZE,
} from "./agentCompanion.constants";
import {
	AgentCompanionAppearance,
	resolveAgentCompanionVariant,
	useAgentCompanion,
} from "./useAgentCompanion";

/**
 * The companion's on-screen appearance.
 *
 * Two rules hold this whole component together, and both exist because the page
 * it lives on is worked in daily and driven by Playwright:
 *
 * - `pointerEvents: "none"` on the container, with no exceptions. it can
 *   never swallow a click meant for the form underneath, so there is no state
 *   in which the mascot blocks someone's work.
 * - `aria-hidden` with no roles, labels or focusable nodes anywhere inside.
 *   Decoration should not be announced, and it should not add a tab stop.
 *
 * Dismissal is therefore by time or by Escape rather than by clicking.
 */

interface AgentCompanionProps {
	appearance: AgentCompanionAppearance | null;
	onDismiss: () => void;
}

export const AgentCompanion: FC<AgentCompanionProps> = ({
	appearance,
	onDismiss,
}) => {
	useEffect(() => {
		if (!appearance) return undefined;
		const onKeyDown = (event: KeyboardEvent) => {
			// No preventDefault: Escape still has to close whatever is open
			// underneath. This only rides along.
			if (event.key === "Escape") onDismiss();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [appearance, onDismiss]);

	if (!appearance) return null;

	const peeking = !!appearance.anchor;
	const size = peeking ? AGENT_COMPANION_PEEK_SIZE : AGENT_COMPANION_FULL_SIZE;

	// Beside the field rather than over it, and clamped into the viewport so a
	// field near the right edge does not push the character off screen. Sits to
	// the left when there is no room on the right.
	const anchoredPosition = ((): { top: number; left: number } | null => {
		const anchor = appearance.anchor;
		if (!anchor) return null;
		const gap = 12;
		const rightOfField = anchor.left + anchor.width + gap;
		const fitsRight =
			typeof window === "undefined" ||
			rightOfField + size <= window.innerWidth - 8;
		return {
			top: anchor.top + anchor.height / 2 - size / 2,
			left: fitsRight ? rightOfField : Math.max(8, anchor.left - size - gap),
		};
	})();

	return (
		<Box
			// Remounting on each appearance restarts the entrance animation.
			key={appearance.id}
			aria-hidden="true"
			data-agent-companion={appearance.trigger}
			sx={{
				position: "fixed",
				...(anchoredPosition
					? { top: anchoredPosition.top, left: anchoredPosition.left }
					: { right: 24, bottom: 24 }),
				display: "flex",
				flexDirection: "column",
				alignItems: "flex-end",
				gap: 1,
				pointerEvents: "none",
				userSelect: "none",
				zIndex: (theme) => theme.zIndex.snackbar,
				animation:
					"agent-companion-enter 520ms cubic-bezier(0.34, 1.4, 0.64, 1) both",
				"@keyframes agent-companion-enter": {
					from: { opacity: 0, transform: "translateY(32px) scale(0.9)" },
					to: { opacity: 1, transform: "none" },
				},
				"@media (prefers-reduced-motion: reduce)": {
					animation: "agent-companion-fade 200ms ease-out both",
					"@keyframes agent-companion-fade": {
						from: { opacity: 0 },
						to: { opacity: 1 },
					},
				},
			}}
		>
			{/* No bubble on a wordless peek — a speech balloon beside an input is
			    in the way in a way the character alone is not. */}
			{!!appearance.line && (
				<Box
					sx={{
						position: "relative",
						maxWidth: 220,
						px: 2,
						py: 1,
						borderRadius: 3,
						borderBottomRightRadius: 4,
						backgroundColor: "background.paper",
						boxShadow: 4,
						// The tail, pointing down at the companion.
						"&::after": {
							content: "''",
							position: "absolute",
							right: 30,
							bottom: -7,
							width: 14,
							height: 14,
							backgroundColor: "background.paper",
							transform: "rotate(45deg)",
						},
					}}
				>
					<Typography variant="body2" sx={{ position: "relative", zIndex: 1 }}>
						{appearance.line}
					</Typography>
				</Box>
			)}

			<Box
				sx={{
					// A slow bob, so a resting companion still feels alive without
					// pulling the eye away from the form.
					animation: "agent-companion-bob 2.6s ease-in-out infinite alternate",
					"@keyframes agent-companion-bob": {
						from: { transform: "translateY(0)" },
						to: { transform: "translateY(-7px)" },
					},
					"@media (prefers-reduced-motion: reduce)": { animation: "none" },
				}}
			>
				<CompanionMascot
					variant={resolveAgentCompanionVariant()}
					expression={appearance.expression}
					size={size}
				/>
			</Box>
		</Box>
	);
};

export { useAgentCompanion };
export default AgentCompanion;
